// src/pages/platform/PlatformProductConfigPage.tsx
// Configuration page for a single platform product — branding, plans, feature flags.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { usePlatform, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Map PlatformProductStatus to Badge variant. */
function statusVariant(
  status: string,
): 'success' | 'secondary' | 'default' | 'warning' | 'error' {
  switch (status) {
    case 'PLATFORM_PRODUCT_STATUS_ENABLED':
      return 'success';
    case 'PLATFORM_PRODUCT_STATUS_DISABLED':
      return 'secondary';
    case 'PLATFORM_PRODUCT_STATUS_COMING_SOON':
      return 'default';
    case 'PLATFORM_PRODUCT_STATUS_BETA':
      return 'warning';
    case 'PLATFORM_PRODUCT_STATUS_DEPRECATED':
      return 'error';
    default:
      return 'secondary';
  }
}

/** Human-readable status label. */
function formatStatus(status: string): string {
  switch (status) {
    case 'PLATFORM_PRODUCT_STATUS_ENABLED':
      return 'Enabled';
    case 'PLATFORM_PRODUCT_STATUS_DISABLED':
      return 'Disabled';
    case 'PLATFORM_PRODUCT_STATUS_COMING_SOON':
      return 'Coming Soon';
    case 'PLATFORM_PRODUCT_STATUS_BETA':
      return 'Beta';
    case 'PLATFORM_PRODUCT_STATUS_DEPRECATED':
      return 'Deprecated';
    default:
      return status.replace('PLATFORM_PRODUCT_STATUS_', '').replace(/_/g, ' ');
  }
}

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface FormState {
  // General
  display_order: number;
  is_featured: boolean;
  max_tenants: number;
  // Branding
  name_override: string;
  tagline_override: string;
  description_override: string;
  icon_url: string;
  logo_url: string;
  color_primary: string;
  color_secondary: string;
  // Plans
  available_plan_slugs: string;
  hidden_plan_slugs: string;
  // Feature flags
  feature_flags: Array<{ key: string; value: boolean }>;
}

function buildInitialForm(product: any): FormState {
  const branding = product.branding ?? {};
  const flags: Record<string, boolean> = product.feature_flags ?? {};

  return {
    display_order: product.display_order ?? 0,
    is_featured: product.is_featured ?? false,
    max_tenants: product.max_tenants ?? 0,

    name_override: branding.name_override ?? '',
    tagline_override: branding.tagline_override ?? '',
    description_override: branding.description_override ?? '',
    icon_url: branding.icon_url ?? '',
    logo_url: branding.logo_url ?? '',
    color_primary: branding.color_primary ?? '',
    color_secondary: branding.color_secondary ?? '',

    available_plan_slugs: (product.available_plan_slugs ?? []).join(', '),
    hidden_plan_slugs: (product.hidden_plan_slugs ?? []).join(', '),

    feature_flags: Object.entries(flags).map(([key, value]) => ({
      key,
      value: Boolean(value),
    })),
  };
}

function formToPayload(form: FormState) {
  const splitTrim = (s: string) =>
    s
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  const flagsMap: Record<string, boolean> = {};
  for (const f of form.feature_flags) {
    if (f.key.trim()) flagsMap[f.key.trim()] = f.value;
  }

  return {
    display_order: form.display_order,
    is_featured: form.is_featured,
    max_tenants: form.max_tenants,
    branding: {
      name_override: form.name_override,
      tagline_override: form.tagline_override,
      description_override: form.description_override,
      icon_url: form.icon_url,
      logo_url: form.logo_url,
      color_primary: form.color_primary,
      color_secondary: form.color_secondary,
    },
    available_plan_slugs: splitTrim(form.available_plan_slugs),
    hidden_plan_slugs: splitTrim(form.hidden_plan_slugs),
    feature_flags: flagsMap,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * PlatformProductConfigPage - configure a single product on a platform.
 * Route: /platforms/:id/products/:productId
 */
export const PlatformProductConfigPage = observer(() => {
  const { id: platformId, productId } = useParams<{
    id: string;
    productId: string;
  }>();
  const navigate = useNavigate();
  const store = usePlatform();
  const { t } = useI18n();

  const [product, setProduct] = useState<any>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [newFlagKey, setNewFlagKey] = useState('');
  const [newFlagValue, setNewFlagValue] = useState(false);

  // Fetch platform (for breadcrumb) + product data.
  useEffect(() => {
    if (!platformId || !productId) return;
    store.GetPlatform(platformId);
    store.GetPlatformProduct(platformId, productId).then((res: any) => {
      const p = res?.product ?? res;
      setProduct(p);
      setForm(buildInitialForm(p));
    }).catch(() => {});

    return () => {
      store.clearCurrent();
    };
  }, [platformId, productId]);

  const platformName = (store.currentItem as any)?.name ?? t('platform.details');
  const productName =
    product?.branding?.name_override || product?.product?.id || productId || '';
  const status = product?.status ?? '';

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleToggle = async () => {
    if (!platformId || !productId) return;
    setTogglingStatus(true);
    try {
      if (status === 'PLATFORM_PRODUCT_STATUS_ENABLED') {
        await store.DisablePlatformProduct(platformId, productId, {});
      } else {
        await store.EnablePlatformProduct(platformId, productId, {});
      }
      // Refresh product
      const res: any = await store.GetPlatformProduct(platformId, productId);
      const p = res?.product ?? res;
      setProduct(p);
    } catch {
      // Error handled by store
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleSave = async () => {
    if (!platformId || !productId || !form) return;
    setIsSubmitting(true);
    setSaved(false);
    try {
      await store.UpdatePlatformProduct(platformId, productId, {
        platform_product: formToPayload(form) as any,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const addFlag = () => {
    if (!form || !newFlagKey.trim()) return;
    updateField('feature_flags', [
      ...form.feature_flags,
      { key: newFlagKey.trim(), value: newFlagValue },
    ]);
    setNewFlagKey('');
    setNewFlagValue(false);
  };

  const removeFlag = (index: number) => {
    if (!form) return;
    updateField(
      'feature_flags',
      form.feature_flags.filter((_, i) => i !== index),
    );
  };

  const toggleFlagValue = (index: number) => {
    if (!form) return;
    const updated = [...form.feature_flags];
    updated[index] = { ...updated[index], value: !updated[index].value };
    updateField('feature_flags', updated);
  };

  // ---------------------------------------------------------------------------
  // Loading / Error / Not-found guards
  // ---------------------------------------------------------------------------

  if (store.isLoading && !product) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (!form) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link
          to={`/platforms/${platformId}/products`}
          className="mt-2 inline-block text-sm underline text-primary"
        >
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/platforms" className="text-muted-foreground hover:text-foreground">
              {t('platform.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link
              to={`/platforms/${platformId}`}
              className="text-muted-foreground hover:text-foreground"
            >
              {platformName}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link
              to={`/platforms/${platformId}/products`}
              className="text-muted-foreground hover:text-foreground"
            >
              {t('platform.products.title')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{productName}</span>
          </nav>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{productName}</h1>
            <Badge variant={statusVariant(status)} size="sm">
              {formatStatus(status)}
            </Badge>
          </div>
        </div>
        <Button
          variant={status === 'PLATFORM_PRODUCT_STATUS_ENABLED' ? 'danger' : 'primary'}
          disabled={togglingStatus}
          onClick={handleToggle}
        >
          {togglingStatus
            ? t('common.loading')
            : status === 'PLATFORM_PRODUCT_STATUS_ENABLED'
              ? t('platform.products.disable')
              : t('platform.products.enable')}
        </Button>
      </div>

      {/* Success / Error */}
      {saved && (
        <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>
          {t('platform.products.saveSuccess')}
        </Alert>
      )}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* ================================================================= */}
      {/* General */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.products.general')}</CardTitle>
          <CardDescription>
            {t('platform.products.generalDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label={t('platform.products.displayOrder')}
            type="number"
            value={String(form.display_order)}
            onChange={(e) => updateField('display_order', Number(e.target.value) || 0)}
          />

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.is_featured}
              onChange={(e) => updateField('is_featured', e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
            />
            <span className="text-sm text-foreground">
              {t('platform.products.isFeatured')}
            </span>
          </label>

          <div>
            <Input
              label={t('platform.products.maxTenants')}
              type="number"
              value={String(form.max_tenants)}
              onChange={(e) => updateField('max_tenants', Number(e.target.value) || 0)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('platform.products.maxTenantsHint')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Branding Overrides */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.products.branding')}</CardTitle>
          <CardDescription>
            {t('platform.products.brandingDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            label={t('platform.products.nameOverride')}
            type="text"
            value={form.name_override}
            onChange={(e) => updateField('name_override', e.target.value)}
            placeholder={t('platform.products.nameOverridePlaceholder')}
          />

          <Input
            label={t('platform.products.taglineOverride')}
            type="text"
            value={form.tagline_override}
            onChange={(e) => updateField('tagline_override', e.target.value)}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('platform.products.descriptionOverride')}
            </label>
            <textarea
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px]"
              value={form.description_override}
              onChange={(e) => updateField('description_override', e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t('platform.products.iconUrl')}
              type="text"
              value={form.icon_url}
              onChange={(e) => updateField('icon_url', e.target.value)}
              placeholder="https://..."
            />
            <Input
              label={t('platform.products.logoUrl')}
              type="text"
              value={form.logo_url}
              onChange={(e) => updateField('logo_url', e.target.value)}
              placeholder="https://..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('platform.products.colorPrimary')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="flex flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring h-9"
                  value={form.color_primary}
                  onChange={(e) => updateField('color_primary', e.target.value)}
                  placeholder="#6366f1"
                />
                {form.color_primary && (
                  <div
                    className="w-9 h-9 rounded-md border border-input flex-shrink-0"
                    style={{ backgroundColor: form.color_primary }}
                  />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                {t('platform.products.colorSecondary')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="flex flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring h-9"
                  value={form.color_secondary}
                  onChange={(e) => updateField('color_secondary', e.target.value)}
                  placeholder="#a5b4fc"
                />
                {form.color_secondary && (
                  <div
                    className="w-9 h-9 rounded-md border border-input flex-shrink-0"
                    style={{ backgroundColor: form.color_secondary }}
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Plans */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.products.plans')}</CardTitle>
          <CardDescription>
            {t('platform.products.plansDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Input
              label={t('platform.products.availablePlans')}
              type="text"
              value={form.available_plan_slugs}
              onChange={(e) => updateField('available_plan_slugs', e.target.value)}
              placeholder="free, pro, enterprise"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('platform.products.plansHint')}
            </p>
          </div>

          <div>
            <Input
              label={t('platform.products.hiddenPlans')}
              type="text"
              value={form.hidden_plan_slugs}
              onChange={(e) => updateField('hidden_plan_slugs', e.target.value)}
              placeholder="internal, legacy"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('platform.products.hiddenPlansHint')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Feature Flags */}
      {/* ================================================================= */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.products.featureFlags')}</CardTitle>
          <CardDescription>
            {t('platform.products.featureFlagsDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Existing flags */}
          {form.feature_flags.length > 0 && (
            <div className="divide-y divide-border rounded-md border border-border">
              {form.feature_flags.map((flag, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={flag.value}
                        onChange={() => toggleFlagValue(index)}
                        className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                      />
                      <span className="text-sm font-mono text-foreground truncate">
                        {flag.key}
                      </span>
                    </label>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive flex-shrink-0"
                    onClick={() => removeFlag(index)}
                  >
                    {t('common.remove')}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {form.feature_flags.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t('platform.products.noFlags')}
            </p>
          )}

          {/* Add new flag */}
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Input
                label={t('platform.products.flagKey')}
                type="text"
                value={newFlagKey}
                onChange={(e) => setNewFlagKey(e.target.value)}
                placeholder="enable_ai_features"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addFlag();
                  }
                }}
              />
            </div>
            <label className="flex items-center gap-2 pb-2">
              <input
                type="checkbox"
                checked={newFlagValue}
                onChange={(e) => setNewFlagValue(e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
              />
              <span className="text-sm text-foreground">
                {t('platform.products.flagEnabled')}
              </span>
            </label>
            <Button variant="secondary" size="sm" onClick={addFlag} disabled={!newFlagKey.trim()}>
              {t('platform.products.addFlag')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* Save */}
      {/* ================================================================= */}
      <div className="flex justify-end gap-3">
        <Button
          variant="secondary"
          onClick={() => navigate(`/platforms/${platformId}/products`)}
        >
          {t('common.cancel')}
        </Button>
        <Button disabled={isSubmitting} onClick={handleSave}>
          {isSubmitting ? t('common.saving') : t('common.saveChanges')}
        </Button>
      </div>
    </div>
  );
});

export default PlatformProductConfigPage;
